'use client';

import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef, type ReactNode } from 'react';
import { Cite } from '../Cite';

/**
 * Allowed heading levels for `CitedHeading`.
 *
 * Per §11b2 (Typography Scale & Hierarchy) the page title is `h5`, so a
 * subordinate `CitedHeading` should generally use level 5 or 6. We still
 * accept 2–4 so the same primitive can render a section landing inside an
 * explicit hero (e.g., Credit Memo cover, Astor reasoning detail).
 */
export type CitedHeadingLevel = 2 | 3 | 4 | 5 | 6;

export interface CitedHeadingProps {
  /** Heading level — maps directly to Typography variant `h{level}`. */
  level: CitedHeadingLevel;
  /** Heading text content. */
  children: ReactNode;
  /**
   * Optional citation indices rendered after the heading text on the same
   * baseline (e.g., `Origination criteria [1][2]`).
   */
  citations?: number[];
  /**
   * Optional click handler fired when a citation marker is clicked. Receives
   * the citation index. Wires through to the host's citation panel.
   */
  onCitationClick?: (n: number) => void;
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

/**
 * `CitedHeading` — heading variant with optional inline `<Cite>` markers.
 *
 * Maps `level` (2 | 3 | 4 | 5 | 6) to the matching `Typography` variant
 * (h2 → h2, etc.) and renders citation markers on the same baseline as the
 * heading text. Designed for Credit Memo section titles and Astor cited
 * answer blocks.
 *
 * Per §11b2, no subordinate element on a page may exceed `h5` unless inside
 * an explicit hero. Choose `level={5}` for in-page section heads.
 */
export const CitedHeading = forwardRef<HTMLHeadingElement, CitedHeadingProps>(function CitedHeading(
  { level, children, citations, onCitationClick, sx },
  ref,
) {
  // Map level → Typography variant + semantic HTML tag.
  const variant = `h${level}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  // Build base sx; consumer sx wins via array composition.
  const baseSx = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: 0.5,
    // Preserve the variant's intrinsic margin behavior — Typography handles
    // top/bottom rhythm via theme.typography[hN]. We don't override it here.
  };
  const sxArray: SxProps<Theme> = sx == null ? baseSx : [baseSx, ...(Array.isArray(sx) ? sx : [sx])];

  const hasCitations = Array.isArray(citations) && citations.length > 0;

  return (
    <Typography ref={ref} component={variant} variant={variant} sx={sxArray}>
      <span>{children}</span>
      {hasCitations && (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
          {citations.map((n) => (
            <Cite key={n} n={n} {...(onCitationClick ? { onClick: onCitationClick } : {})} />
          ))}
        </span>
      )}
    </Typography>
  );
});

CitedHeading.displayName = 'CitedHeading';

export default CitedHeading;
